import { lazy } from 'react';

/**
 * Wraps React.lazy() with automatic retry and page refresh on chunk load failure.
 *
 * After a new deployment, old chunk files (with different hashes) no longer exist.
 * Users with a long-lived tab try to load chunks that return 404 — or, because the
 * SPA catch-all rewrite serves index.html for any unmatched path, a 200 of text/html,
 * which the module loader rejects as "Expected a JavaScript-or-Wasm module script".
 * Either way the dynamic import rejects and the screen would white-out.
 *
 * This utility catches those errors, shows a friendly "valet re-arranging" message,
 * and refreshes the page to get the new index.html.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * 2026-08-18 — WHY THE RECOVERY RACES ITSELF IF YOU CHANGE THIS
 *
 * React re-invokes a rejected lazy factory on every subsequent render attempt.
 * The original version set the sessionStorage flag, showed the overlay, then
 * `await`ed 800ms before reloading. Inside that window React called the factory
 * again; the second call read the flag as already-set, fell through to the
 * "we already tried" branch, REMOVED the flag, and threw — so the error boundary
 * painted a white screen before the reload from the first call ever fired, and
 * the loop guard was cleared on the way out. Doug's 2026-08-18 production crash
 * shows exactly this: the same MIME error six times, then the TypeError landing
 * in react-dom's error handler.
 *
 * Two invariants keep it correct. Do not weaken either:
 *   1. `reloadInFlight` is a MODULE-level latch. Once recovery is committed,
 *      every other caller parks on a never-resolving promise. Nobody throws
 *      while a reload is coming.
 *   2. Reload is issued IMMEDIATELY — no timer between committing and reloading.
 *      The overlay still paints (the browser keeps showing this document until
 *      the new one arrives), so the friendly message costs nothing.
 * ─────────────────────────────────────────────────────────────────────────────
 */

// Committed to a reload. Module-level on purpose: it coordinates every concurrent
// lazy factory and import on the page, which sessionStorage alone cannot do
// (sessionStorage coordinates ACROSS reloads; this coordinates WITHIN one page).
let reloadInFlight = false;

// Handed back to any caller that must not resolve and must not throw.
const NEVER_RESOLVES = () => new Promise(() => {});

// sessionStorage throws in some privacy modes / embedded contexts. The recovery
// path must never be the thing that crashes the app.
function safeGet(key) {
  try {
    return sessionStorage.getItem(key);
  } catch {
    return null;
  }
}

function safeSet(key, value) {
  try {
    sessionStorage.setItem(key, value);
  } catch {
    // Non-fatal: we lose only the across-reload loop guard, not the recovery.
  }
}

function safeRemove(key) {
  try {
    sessionStorage.removeItem(key);
  } catch {
    // Non-fatal.
  }
}

function showValetRearrangingOverlay() {
  // Don't add duplicate overlays
  if (document.getElementById('valet-rearranging-overlay')) return;

  const overlay = document.createElement('div');
  overlay.id = 'valet-rearranging-overlay';
  overlay.innerHTML = `
    <div style="
      position: fixed;
      inset: 0;
      background: rgba(17, 24, 39, 0.95);
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      z-index: 99999;
      font-family: Inter, system-ui, sans-serif;
    ">
      <div style="
        position: relative;
        width: 64px;
        height: 64px;
        margin-bottom: 24px;
      ">
        <div style="
          position: absolute;
          inset: 0;
          border: 3px solid transparent;
          border-top-color: #aea36d;
          border-radius: 50%;
          animation: spin 1s linear infinite;
        "></div>
        <div style="
          position: absolute;
          inset: 0;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 28px;
        ">🚗</div>
      </div>
      <p style="
        color: #9ca3af;
        font-size: 16px;
        margin: 0;
      ">Valet re-arranging vehicles...</p>
      <p style="
        color: #6b7280;
        font-size: 12px;
        margin-top: 8px;
      ">New version detected</p>
    </div>
    <style>
      @keyframes spin {
        to { transform: rotate(360deg); }
      }
    </style>
  `;
  document.body.appendChild(overlay);
}

/**
 * Check if an error is a chunk/module load failure (stale deployment)
 */
function isChunkLoadError(error) {
  const msg = error?.message || '';
  return (
    msg.includes('Failed to fetch dynamically imported module') ||
    msg.includes('error loading dynamically imported module') ||
    msg.includes('Loading chunk') ||
    msg.includes('Loading CSS chunk') ||
    // The SPA catch-all serves index.html for a missing chunk, so the loader
    // reports a MIME rejection rather than a fetch failure.
    msg.includes('Expected a JavaScript-or-Wasm module script') ||
    error?.name === 'ChunkLoadError'
  );
}

/**
 * Commit to a full-page recovery reload. Idempotent: the first caller performs
 * it, every later caller simply parks. Always returns a promise that neither
 * resolves nor rejects, so no caller can surface an error to an error boundary
 * while the reload is on its way.
 */
function commitRecoveryReload(storageKey, label) {
  if (reloadInFlight) return NEVER_RESOLVES();
  reloadInFlight = true;

  safeSet(storageKey, 'true');
  console.log(`[lazyWithRetry] Chunk load failed for ${label}, refreshing page...`);
  showValetRearrangingOverlay();

  // Immediately — never behind a timer. The overlay still paints; the browser
  // keeps this document on screen until the new one arrives.
  window.location.reload();

  return NEVER_RESOLVES();
}

/**
 * Decide what to do with a failed import. Shared by both entry points so the
 * two can never drift apart.
 */
function handleImportFailure(error, storageKey, alreadyRefreshed, label) {
  if (!isChunkLoadError(error)) throw error;

  // A reload is already committed by another concurrent import — park.
  if (reloadInFlight) return NEVER_RESOLVES();

  // First failure on this route: recover.
  if (!alreadyRefreshed) return commitRecoveryReload(storageKey, label);

  // We already reloaded once for this route and it STILL fails. That is real
  // breakage, not a stale deploy. Clear the guard so a later attempt may retry,
  // then surface it to the error boundary honestly.
  safeRemove(storageKey);
  throw error;
}

export function lazyWithRetry(componentImport, componentName = 'Component') {
  return lazy(async () => {
    const storageKey = `chunk-refresh-${window.location.pathname}`;
    const alreadyRefreshed = safeGet(storageKey);

    // A sibling import already committed a reload — don't even attempt.
    if (reloadInFlight) return NEVER_RESOLVES();

    try {
      const component = await componentImport();
      // Success - clear any refresh flag
      safeRemove(storageKey);
      return component;
    } catch (error) {
      return handleImportFailure(error, storageKey, alreadyRefreshed, componentName);
    }
  });
}

/**
 * Chunk-aware wrapper for imperative await import() calls.
 * Use this instead of raw `await import(...)` inside try/catch blocks.
 *
 * On chunk load failure: shows "valet re-arranging" overlay and reloads page.
 * On second failure (already reloaded): throws the original error so caller can handle.
 *
 * Usage:
 *   const { default: OobeoWeeklySync } = await importWithRetry(
 *     () => import('@bricksOS/integrations/services/OobeoWeeklySync.js'),
 *     'OobeoWeeklySync'
 *   );
 */
export async function importWithRetry(importFn, moduleName = 'module') {
  const storageKey = `chunk-refresh-${window.location.pathname}`;
  const alreadyRefreshed = safeGet(storageKey);

  if (reloadInFlight) return NEVER_RESOLVES();

  try {
    const mod = await importFn();
    safeRemove(storageKey);
    return mod;
  } catch (error) {
    return handleImportFailure(error, storageKey, alreadyRefreshed, moduleName);
  }
}

export default lazyWithRetry;
